/**
 * Delivery route optimisation — greedy nearest-neighbour from the depot.
 *
 * Deliberately simple, and deliberately honest about it. This orders a day's
 * stops by repeatedly hopping to the closest unvisited shop, measuring in
 * straight-line (haversine) kilometres. It is NOT a road-network solver: it
 * ignores one-ways, rivers and the actual street grid, so the sequence is a
 * sensible first cut, not a guarantee of the shortest drive. For a tight urban
 * beat the two usually agree; where they do not, the driver has the Manual
 * toggle. Turn-by-turn navigation is handed to Google Maps per stop.
 *
 * Why greedy and not something cleverer: a beat is a few dozen stops, the data
 * is on the phone already, and this runs in under a millisecond with no service
 * to call, no key to hold and no cost per request. If road distances are wanted
 * later, swap the distance function for a routing-service matrix — the ordering
 * loop does not change.
 */

export interface GeoPoint {
  latitude: number;
  longitude: number;
}

export interface RouteStop extends GeoPoint {
  id: string;
}

const R_KM = 6371; // Earth's mean radius.
const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance between two points, in kilometres. */
export function haversineKm(a: GeoPoint, b: GeoPoint): number {
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

export interface OptimisedRoute<T extends RouteStop> {
  /** Stops in visit order. */
  ordered: T[];
  /** Total straight-line distance depot → each stop → … , in km. */
  totalKm: number;
  /** Per-leg distance; legs[i] is the hop that ARRIVES at ordered[i]. */
  legKm: number[];
}

/**
 * Order `stops` by nearest-neighbour starting from `depot`.
 *
 * Stops without usable coordinates are dropped from the optimisation (they
 * cannot be placed on a map or measured); the caller decides how to surface
 * them. The input array is not mutated.
 */
export function optimiseRoute<T extends RouteStop>(depot: GeoPoint, stops: T[]): OptimisedRoute<T> {
  const remaining = stops.filter(
    (s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude),
  );
  const ordered: T[] = [];
  const legKm: number[] = [];
  let here: GeoPoint = depot;
  let totalKm = 0;

  while (remaining.length > 0) {
    let bestIdx = 0;
    let bestKm = Infinity;
    for (let i = 0; i < remaining.length; i += 1) {
      const d = haversineKm(here, remaining[i]!);
      if (d < bestKm) { bestKm = d; bestIdx = i; }
    }
    const [next] = remaining.splice(bestIdx, 1);
    ordered.push(next!);
    legKm.push(bestKm);
    totalKm += bestKm;
    here = next!;
  }

  return { ordered, totalKm, legKm };
}

/** How many of the given stops can actually be placed on the map. */
export function locatableCount(stops: GeoPoint[]): number {
  return stops.filter((s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude)).length;
}

/** A Google Maps directions deep-link for one stop, used by the Navigate button. */
export function navigationUrl(stop: GeoPoint & { locationUrl?: string | null }): string {
  // A location captured on the customer card already carries a Maps URL; prefer
  // it, since it may point at the exact gate rather than the rooftop centroid.
  if (stop.locationUrl) return stop.locationUrl;
  return `https://www.google.com/maps/dir/?api=1&destination=${stop.latitude},${stop.longitude}`;
}
